import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogOut, BadgePercent, ArrowLeft, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STATUS_META = {
  nouveau: { label: "Envoyée", cls: "bg-[#0066FF]/15 text-[#00E5FF] border-[#0066FF]/40" },
  confirme: { label: "Confirmée", cls: "bg-amber-500/15 text-amber-400 border-amber-500/40" },
  traite: { label: "Traitée", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" },
};

const INPUT_CLASS =
  "w-full rounded-md border border-white/10 bg-[#07090E] px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#0066FF] focus:shadow-[0_0_0_3px_rgba(0,102,255,0.2)]";

const PerkCard = () => (
  <div data-testid="account-perk-card" className="rounded-md border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-5 flex items-start gap-4">
    <BadgePercent className="w-6 h-6 text-[#F59E0B] shrink-0 mt-0.5" />
    <div>
      <div className="font-display font-bold text-lg text-[#F59E0B]">-40% sur votre première prestation</div>
      <p className="text-sm text-slate-300 mt-1 leading-relaxed">
        Offre réservée aux comptes clients, valable sur la première prestation à l'atelier.
        Suivez ensuite vos demandes de rendez-vous et de devis en ligne.
      </p>
    </div>
  </div>
);

const AuthForms = () => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ nom: "", email: "", telephone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (mode === "register") {
        const payload = { nom: form.nom, email: form.email, password: form.password };
        if (form.telephone) payload.telephone = form.telephone;
        await register(payload);
        toast.success("Compte créé ! Votre offre -40% est activée.");
      } else {
        await login(form.email, form.password);
        toast.success("Bon retour parmi nous !");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-md border border-white/10 bg-[#0E1322]/80 backdrop-blur-xl p-6 md:p-8">
      <div className="flex gap-2 mb-7 bg-[#07090E] rounded-full p-1">
        {[
          ["register", "Créer mon compte"],
          ["login", "Me connecter"],
        ].map(([key, label]) => (
          <button
            key={key}
            data-testid={`account-tab-${key}`}
            onClick={() => {
              setMode(key);
              setError("");
            }}
            className={`flex-1 rounded-full py-2.5 text-sm font-semibold transition-colors duration-300 ${
              mode === key ? "bg-[#0066FF] text-white" : "text-slate-400 hover:text-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-4">
        {mode === "register" && (
          <input data-testid="account-input-nom" required minLength={2} value={form.nom} onChange={set("nom")} placeholder="Votre nom" className={INPUT_CLASS} />
        )}
        <input data-testid="account-input-email" type="email" required value={form.email} onChange={set("email")} placeholder="Email" className={INPUT_CLASS} />
        {mode === "register" && (
          <input data-testid="account-input-telephone" type="tel" value={form.telephone} onChange={set("telephone")} placeholder="Téléphone (optionnel)" className={INPUT_CLASS} />
        )}
        <input data-testid="account-input-password" type="password" required minLength={8} value={form.password} onChange={set("password")} placeholder="Mot de passe (8 caractères min.)" className={INPUT_CLASS} />
        {error && (
          <div data-testid="account-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-4 py-2.5">
            {error}
          </div>
        )}
        <button
          data-testid="account-submit-button"
          type="submit"
          disabled={loading}
          className="w-full rounded-full bg-[#0066FF] hover:bg-[#0052CC] disabled:opacity-60 text-white font-semibold py-3 flex items-center justify-center gap-2 transition-colors duration-300"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {mode === "register" ? "Créer mon compte (-40% offerts)" : "Me connecter"}
        </button>
      </form>
    </div>
  );
};

const MesDemandes = ({ user, logout }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/mes-demandes")
      .then(({ data }) => setRequests(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <div className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#00E5FF]">Espace client</div>
          <h1 className="font-display font-extrabold uppercase tracking-tight text-2xl md:text-3xl mt-2">
            Bonjour {user.name}
          </h1>
          <div data-testid="account-user-email" className="text-sm text-slate-400 mt-1">{user.email}</div>
        </div>
        <div className="flex items-center gap-3">
          {user.role === "admin" && (
            <Link data-testid="account-admin-link" to="/admin" className="rounded-full border border-white/15 hover:border-[#0066FF] px-5 py-2.5 text-sm font-semibold transition-colors duration-300">
              Espace Pro
            </Link>
          )}
          <button
            data-testid="account-logout-button"
            onClick={logout}
            className="rounded-full border border-white/15 hover:border-red-500 hover:text-red-400 px-5 py-2.5 text-sm font-semibold flex items-center gap-2 transition-colors duration-300"
          >
            <LogOut className="w-4 h-4" />
            Déconnexion
          </button>
        </div>
      </div>

      <div className="mb-8">
        <PerkCard />
      </div>

      <h2 className="font-display font-bold text-xl mb-5">Mes demandes</h2>
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-[#0066FF]" />
        </div>
      ) : requests.length === 0 ? (
        <div data-testid="account-empty-requests" className="rounded-md border border-white/10 bg-[#0E1322] py-16 text-center">
          <Inbox className="w-8 h-8 text-[#0066FF] mx-auto mb-4" />
          <p className="text-slate-400 text-sm">Aucune demande pour le moment.</p>
          <Link to="/" className="inline-block mt-4 text-sm text-[#00E5FF] hover:underline">
            Faire une demande de devis
          </Link>
        </div>
      ) : (
        <div data-testid="account-requests-list" className="space-y-4">
          {requests.map((r) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid={`account-request-${r.id}`}
              className="rounded-md border border-white/10 bg-[#0E1322] p-6"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-display font-bold text-lg">{r.service}</span>
                  <span className={`rounded-full border px-3 py-0.5 text-[10px] font-mono-tech uppercase tracking-[0.2em] ${STATUS_META[r.status]?.cls || STATUS_META.nouveau.cls}`}>
                    {STATUS_META[r.status]?.label || r.status}
                  </span>
                </div>
                <span className="text-xs text-slate-500 font-mono-tech">{new Date(r.created_at).toLocaleString("fr-FR")}</span>
              </div>
              {r.date_souhaitee && <div className="text-sm text-slate-400 mt-2">Date souhaitée : {r.date_souhaitee}</div>}
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{r.message}</p>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Account() {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-[#07090E] text-[#F8FAFC] noise-overlay relative">
      <div className="max-w-3xl mx-auto px-6 py-10 md:py-16">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" data-testid="account-back-home" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors duration-300">
            <ArrowLeft className="w-4 h-4" />
            Retour au site
          </Link>
          <span className="rounded-md bg-white px-2 py-1 flex items-center">
            <img src="/logo-emblem.png" alt="Logo AMP Services 01" className="h-7 w-auto" />
          </span>
        </div>

        {user === undefined ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-[#0066FF]" />
          </div>
        ) : user ? (
          <MesDemandes user={user} logout={logout} />
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="text-center mb-8">
              <div className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#00E5FF]">Espace client</div>
              <h1 className="font-display font-extrabold uppercase tracking-tight text-2xl md:text-3xl mt-2">Votre compte AMP</h1>
            </div>
            <div className="mb-6">
              <PerkCard />
            </div>
            <AuthForms />
          </motion.div>
        )}
      </div>
    </div>
  );
}
